import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useVoteData } from '../../context/VoteDataContext';
import { useSettings } from '../../context/SettingsContext';
import { useHandicap } from '../../context/HandicapContext';
import SideNav from '../SideNav/SideNav';
import PlayerLeaderboardTable from './PlayerLeaderboardTable';
import PlayerVoteSummaryTable from './PlayerVoteSummaryTable';
import FilterToolsModal from './FilterToolsModal';

const selectArrowStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
  backgroundSize: '14px',
};

const selectClass =
  'bg-slate-900/50 border border-slate-700/60 text-xs font-mono text-white rounded-lg px-3 h-10 focus:outline-none focus:border-emerald-500/60 cursor-pointer appearance-none pr-8 transition-colors';

const ghostButtonClass =
  'text-xs font-mono font-medium rounded-lg border border-slate-700/70 text-slate-300 hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all';

// Presentational-only helper: identical controlled input + clear button as before,
// styled once instead of in every layout branch.
const SearchPlayersInput = ({ value, onChange, className = '' }) => (
  <div className={`relative min-w-0 ${className}`}>
    <svg
      className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <input
      type="text"
      placeholder="Search players..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-slate-900/50 border border-slate-700/70 rounded-lg pl-8 pr-7 h-10 text-xs font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/60 transition-colors w-full"
    />
    {value && (
      <button
        onClick={() => onChange('')}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-200 transition-colors"
        aria-label="Clear search"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    )}
  </div>
);

// Segmented view toggle (Vote Leaderboard | Vote Summary).
// Full-width (mobile) mode drops the "Vote" prefix so both options fit.
const ViewToggle = ({ activeView, setActiveView, fullWidth = false }) => (
  <div className={`flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-950/60 border border-slate-800/70 ${fullWidth ? 'w-full' : ''}`}>
    {[
      { id: 'leaderboard', label: 'Vote Leaderboard', shortLabel: 'Leaderboard' },
      { id: 'summary', label: 'Vote Summary', shortLabel: 'Summary' },
    ].map((v) => (
      <button
        key={v.id}
        onClick={() => setActiveView(v.id)}
        className={`${fullWidth ? 'flex-1' : ''} px-2.5 py-1.5 rounded-md text-[11px] font-mono font-semibold whitespace-nowrap transition-all ${
          activeView === v.id
            ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
            : 'text-slate-400 border border-transparent hover:text-slate-200'
        }`}
      >
        {fullWidth ? v.shortLabel : v.label}
      </button>
    ))}
  </div>
);

// Handicap chip: label + switch + optional reset
const HandicapControls = ({ enabled, setEnabled, onReset, resetLabel, className = '' }) => (
  <div className={`flex items-center gap-2.5 bg-slate-900/50 border border-slate-700/60 rounded-lg px-3 h-10 ${className}`}>
    <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-slate-500">Handicap</span>
    <button
      onClick={() => setEnabled(!enabled)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:ring-offset-2 focus:ring-offset-slate-900 ${
        enabled ? 'bg-emerald-500' : 'bg-slate-700'
      }`}
    >
      <span
        className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
    {enabled && (
      <button
        onClick={onReset}
        className="px-2 py-0.5 text-[10px] font-mono font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded hover:bg-emerald-500/15 hover:border-emerald-500/50 transition-all"
        title="Reset all handicaps"
      >
        {resetLabel}
      </button>
    )}
  </div>
);

const Dashboard = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { data: voteData, isLoading: isVoteLoading, error: voteError, fetchVoteData } = useVoteData();
  // Fetch vote data once after successful login
  useEffect(() => {
    const t = token && token();
    if (isAuthenticated && t && !voteData) {
      fetchVoteData(t);
    }
  }, [isAuthenticated, token, voteData, fetchVoteData]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('leaderboard'); // 'leaderboard' | 'summary'
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  // Filtering state: single point or range
  const [filterFrom, setFilterFrom] = useState(null); // 0..24 or null
  const [filterTo, setFilterTo] = useState(null); // 0..24 or null
  const [leaderboardMode, setLeaderboardMode] = useState('votes'); // 'votes' | 'finish' | 'xplus'
  const { selectedDataset, setSelectedDataset, getEnabledKeys } = useSettings();
  
  // Handicap context
  const { resetTeamHandicaps } = useHandicap();
  const [handicapEnabled, setHandicapEnabled] = useState(false);
  const enabledDatasetKeys = getEnabledKeys();
  const datasetKeyToLabel = {
    count1: 'BG 1',
    count2: 'BG 2',
    count3: 'BG 3',
    count4: 'BG 4',
    count5: 'BG 5',
    betfair: 'Betfair',
    espn: 'ESPN',
    byb: 'BYB',
  };
  useEffect(() => {
    if (selectedDataset !== 'all' && !enabledDatasetKeys.includes(selectedDataset)) {
      setSelectedDataset('all');
    }
  }, [enabledDatasetKeys, selectedDataset, setSelectedDataset]);
  // Vote Frequency config
  const [voteFrequencyEnabled, setVoteFrequencyEnabled] = useState(false);
  const [freqThresholds, setFreqThresholds] = useState({
    polledMin: 0.7,
    threeMin: 2.4,
    twoMin: 1.4,
    twoMax: 2.4,
    oneMin: 0.7,
    oneMax: 1.4,
  });

  const handleModeChange = (e) => {
    const mode = e.target.value;
    setLeaderboardMode(mode);
    // Reset all filters whenever mode changes
    setFilterFrom(null);
    setFilterTo(null);
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white">
      {/* Navigation */}
      <SideNav 
        isMobileMenuOpen={isMobileMenuOpen} 
        setIsMobileMenuOpen={setIsMobileMenuOpen} 
      />

      {/* Dashboard Content */}
      <div className="md:ml-[var(--sidenav-width)] md:transition-[margin-left] md:duration-200 md:ease-out pt-3 md:pt-6 pb-20 md:pb-0">
        {isVoteLoading && (
          <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-3 md:px-6">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-700 border-t-green-400" />
          </div>
        )}
        {voteError && (
          <div className="px-3 md:px-6 pb-6">
            <div className="bg-red-900/30 border border-red-500 text-red-300 rounded-lg p-3 text-sm">{voteError}</div>
          </div>
        )}
        {/* {!isVoteLoading && !voteError && !voteData && (
          <div className="px-3 md:px-6 pb-6">
            <div className="bg-yellow-900/30 border border-yellow-500 text-yellow-300 rounded-lg p-3 text-sm">Vote data not loaded.</div>
          </div>
        )} */}
        {!isVoteLoading && (
        <div className="px-3 md:px-6 pb-6">
          {/* Controls */}
          {/* Mobile layout */}
          <div className="md:hidden mb-4 space-y-3">
            {/* Row 1: Toggle */}
            <ViewToggle activeView={activeView} setActiveView={setActiveView} fullWidth />

            {/* Row 2 (mobile only): Leaderboard view layout depends on filter state */}
            {activeView === 'leaderboard' ? (
              (filterFrom !== null || filterTo !== null) ? (
                // FILTERS APPLIED: flip rows → first Filter Tools|Clear, then Search|Mode
                <>
                  {/* Row 1: Filter Tools | Clear (when active) */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <button
                        onClick={() => setIsFilterModalOpen(true)}
                        className={`w-full h-10 px-3 ${ghostButtonClass}`}
                        title={'Configure filters'}
                      >
                        Filters & Tools
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={() => {
                          setFilterFrom(null);
                          setFilterTo(null);
                        }}
                        className={`w-full h-10 px-3 ${ghostButtonClass}`}
                        title="Clear active filter"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  {/* Row 2: Search | Handicap */}
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <SearchPlayersInput value={searchTerm} onChange={setSearchTerm} className="w-full" />
                    <HandicapControls
                      enabled={handicapEnabled}
                      setEnabled={setHandicapEnabled}
                      onReset={() => resetTeamHandicaps('leaderboard')}
                      resetLabel="Reset"
                      className="justify-center"
                    />
                  </div>
                  {/* Row 3: Mode selector full width */}
                  <div className="mt-2">
                    <select
                      value={leaderboardMode}
                      onChange={handleModeChange}
                      className={`${selectClass} w-full`}
                      style={selectArrowStyle}
                    >
                      <option value="votes">Projected Votes</option>
                      <option value="finish">Finishing Position</option>
                      <option value="xplus">X+ Votes</option>
                    </select>
                  </div>
                </>
              ) : (
                // NO FILTERS: flip rows → Row 1 two-column: Filter Tools | Mode, Row 2 search full width
                <>
                  {/* Row 1: Filter Tools | Mode */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <button
                        onClick={() => setIsFilterModalOpen(true)}
                        className={`w-full h-10 px-3 ${ghostButtonClass}`}
                        title={'Configure filters'}
                      >
                        Filters & Tools
                      </button>
                    </div>
                    <div>
                      <select
                        value={leaderboardMode}
                        onChange={handleModeChange}
                        className={`${selectClass} w-full`}
                        style={selectArrowStyle}
                      >
                        <option value="votes">Projected Votes</option>
                        <option value="finish">Finishing Position</option>
                        <option value="xplus">X+ Votes</option>
                      </select>
                    </div>
                  </div>
                  {/* Row 2: Search and Handicap inline */}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <SearchPlayersInput value={searchTerm} onChange={setSearchTerm} className="w-full" />
                    <HandicapControls
                      enabled={handicapEnabled}
                      setEnabled={setHandicapEnabled}
                      onReset={() => resetTeamHandicaps('leaderboard')}
                      resetLabel="Reset"
                      className="justify-center"
                    />
                  </div>
                </>
              )
            ) : (
              // Summary view (mobile): mirror leaderboard mobile layout with Dataset instead of Mode
              (filterFrom !== null || filterTo !== null) ? (
                // FILTERS APPLIED: Row 1 Filter Tools | Clear, Row 2 Search (60%) | Dataset (40%)
                <>
                  {/* Row 1: Filter Tools | Clear */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <button
                        onClick={() => setIsFilterModalOpen(true)}
                        className={`w-full h-10 px-3 ${ghostButtonClass}`}
                        title={'Configure filters'}
                      >
                        Filters & Tools
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={() => {
                          setFilterFrom(null);
                          setFilterTo(null);
                        }}
                        className={`w-full h-10 px-3 ${ghostButtonClass}`}
                        title="Clear active filter"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  {/* Row 2: Search (60%) | Dataset (40%) */}
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    <div className="col-span-3">
                      <SearchPlayersInput value={searchTerm} onChange={setSearchTerm} className="w-full" />
                    </div>
                    <div className="col-span-2">
                      <select
                        id="summary-dataset-select-mobile"
                        value={selectedDataset}
                        onChange={(e) => setSelectedDataset(e.target.value)}
                        className={`${selectClass} w-full`}
                        style={selectArrowStyle}
                        title="Choose dataset for Vote Summary"
                      >
                        <option value="all">Dataset: All</option>
                        {enabledDatasetKeys.map((k) => (
                          <option key={k} value={k}>Dataset: {datasetKeyToLabel[k] || k}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                // NO FILTERS: Row 1 two-column: Filter Tools | Dataset, Row 2 full-width Search
                <>
                  {/* Row 1: Filter Tools | Dataset */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <button
                        onClick={() => setIsFilterModalOpen(true)}
                        className={`w-full h-10 px-3 ${ghostButtonClass}`}
                        title={'Configure filters'}
                      >
                        Filters & Tools
                      </button>
                    </div>
                    <div>
                      <select
                        id="summary-dataset-select-mobile"
                        value={selectedDataset}
                        onChange={(e) => setSelectedDataset(e.target.value)}
                        className={`${selectClass} w-full`}
                        style={selectArrowStyle}
                        title="Choose dataset for Vote Summary"
                      >
                        <option value="all">Dataset: All</option>
                        {enabledDatasetKeys.map((k) => (
                          <option key={k} value={k}>Dataset: {datasetKeyToLabel[k] || k}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/* Row 2: Search full width */}
                  <div className="mt-2">
                    <SearchPlayersInput value={searchTerm} onChange={setSearchTerm} className="w-full" />
                  </div>
                </>
              )
            )}
          </div>

          {/* Desktop layout */}
          <div className="hidden md:flex md:items-center md:justify-between gap-3 mb-4">
            {/* Search + Mode */}
            <div className="w-full md:w-auto flex items-center gap-2">
              <SearchPlayersInput value={searchTerm} onChange={setSearchTerm} className="w-full md:w-80" />
              {activeView === 'summary' && (
                <select
                  value={selectedDataset}
                  onChange={(e) => setSelectedDataset(e.target.value)}
                  className={selectClass}
                  style={selectArrowStyle}
                  title="Choose dataset for Vote Summary"
                >
                  <option value="all">Dataset: All</option>
                  {enabledDatasetKeys.map((k) => (
                    <option key={k} value={k}>Dataset: {datasetKeyToLabel[k] || k}</option>
                  ))}
                </select>
              )}
              {activeView === 'leaderboard' && (
                <>
                  <select
                    value={leaderboardMode}
                    onChange={handleModeChange}
                    className={selectClass}
                    style={selectArrowStyle}
                  >
                    <option value="votes">Projected Votes</option>
                    <option value="finish">Finishing Position</option>
                    <option value="xplus">X+ Votes</option>
                  </select>
                  
                  {/* Handicap Controls for Desktop */}
                  <HandicapControls
                    enabled={handicapEnabled}
                    setEnabled={setHandicapEnabled}
                    onReset={() => resetTeamHandicaps('leaderboard')}
                    resetLabel="Reset All"
                  />
                </>
              )}
            </div>

            {/* Toggle + tools */}
            <div className="flex items-center gap-2 self-start md:self-auto">
              <ViewToggle activeView={activeView} setActiveView={setActiveView} />
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className={`h-10 px-3 ${ghostButtonClass}`}
                title={'Configure filters'}
              >
                Filters & Tools
              </button>
              {(filterFrom !== null || filterTo !== null) && (
                <button
                  onClick={() => {
                    setFilterFrom(null);
                    setFilterTo(null);
                  }}
                  className={`h-10 px-3 ${ghostButtonClass}`}
                  title="Clear active filter"
                >
                  Clear Filter {filterFrom !== null || filterTo !== null ? `(Rounds ${filterFrom ?? 0}–${filterTo ?? 24})` : ''}
                </button>
              )}
            </div>
          </div>

          {activeView === 'leaderboard' ? (
            <PlayerLeaderboardTable
              searchTerm={searchTerm}
              filterFrom={filterFrom}
              filterTo={filterTo}
              leaderboardMode={leaderboardMode}
              voteFrequencyEnabled={voteFrequencyEnabled}
              freqThresholds={freqThresholds}
              voteDataRemote={voteData}
              handicapEnabled={handicapEnabled}
            />
          ) : (
            <PlayerVoteSummaryTable
              searchTerm={searchTerm}
              filterFrom={filterFrom}
              filterTo={filterTo}
              voteFrequencyEnabled={voteFrequencyEnabled}
              freqThresholds={freqThresholds}
              selectedDataset={selectedDataset}
              voteDataRemote={voteData}
            />
          )}

          <FilterToolsModal
            isOpen={isFilterModalOpen}
            selectedFrom={filterFrom}
            selectedTo={filterTo}
            voteFrequencyEnabled={voteFrequencyEnabled}
            freqThresholds={freqThresholds}
            onSave={({ from, to, voteFrequency }) => {
              setFilterFrom(from);
              setFilterTo(to);
              if (voteFrequency) {
                setVoteFrequencyEnabled(!!voteFrequency.enabled);
                if (voteFrequency.thresholds) setFreqThresholds({ ...voteFrequency.thresholds });
              }
              // Reset dropdown back to Votes whenever a filter is applied
              setLeaderboardMode('votes');
              setIsFilterModalOpen(false);
            }}
            onClear={() => {
              setFilterFrom(null);
              setFilterTo(null);
              setVoteFrequencyEnabled(false);
              setIsFilterModalOpen(false);
            }}
            onClose={() => setIsFilterModalOpen(false)}
          />
        </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
